import { cn } from "@/lib/utils";
import { Tooltip as TooltipPrimitive } from "radix-ui";
import type { ReactNode } from "react";

/** A controlled hover tooltip — unlike the native `title` attribute (whose ~700ms+ open delay the browser
 *  fixes and we can't tune), this exposes `delayDuration` so hints can appear roughly twice as fast. Renders
 *  the app's own dark bubble so it matches the rest of the UI. If `content` is empty it renders the child
 *  untouched (no trigger), so callers can gate the tooltip inline. */
export function Tooltip({
  content,
  children,
  delayDuration = 300,
  side = "top",
  className,
}: {
  content: ReactNode;
  children: ReactNode;
  delayDuration?: number;
  side?: "top" | "right" | "bottom" | "left";
  className?: string;
}) {
  if (!content) return <>{children}</>;
  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={6}
            className={cn(
              "z-50 max-w-xs rounded-md bg-foreground px-2.5 py-1.5 text-xs leading-snug text-background shadow-md",
              "data-[state=delayed-open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=delayed-open]:fade-in data-[state=delayed-open]:zoom-in-95",
              className,
            )}
          >
            {content}
            <TooltipPrimitive.Arrow className="fill-foreground" />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
